"use client";

import React, { useEffect, useState } from "react";
import { heroImageDesktopUrl, shopByOwnerBaseUrl } from "@/constants/config";
import { buildShopUrl as buildShopUrlUtil, toShopSlug as toComparableValue } from "@/utilities/buildShopUrl";
import { useMakeDropdown } from "@/hooks/useMakeDropdown";
import { useModelDropdown } from "@/hooks/useModelDropdown";
import { fetchModelsByMake } from "@/utilities/dropdownHelpers";
import { normalizeZip, isValidZip } from "@/utilities/zipHelpers";
import { queryLocationByZip } from "@/utilities/locationApi";
import {
  Button,
  Box,
  Grid,
  GridItem,
  TextInput,
  Title,
  Body,
  SelectDropdown,
  Flex,
} from "@iris/react-components";
import { sendClick } from 'reaxl-analytics';

const shopPrivateSellerBaseUrl = shopByOwnerBaseUrl;

type BuyerLandingHeroRedesignProps = Readonly<{
  currentZip?: string;
  onZipChange?: (zip: string) => void;
}>;

type HeroSelectOption = {
  value: string;
  label: string;
};

type HeroSelectOptionGroup = {
  optgroup: string;
  options: HeroSelectOption[];
};

type IrisSelectOptionGroup = {
  label?: string;
  children: HeroSelectOption[];
};

const findOptionByValue = (
  optionGroups: IrisSelectOptionGroup[],
  targetValue: string,
): HeroSelectOption | null => {
  const comparableTarget = toComparableValue(targetValue);

  for (const group of optionGroups) {
    const matched = group.children.find((option) => {
      const comparableValue = toComparableValue(option.value || "");
      const comparableLabel = toComparableValue(option.label || "");

      return (
        option.value === targetValue ||
        comparableValue === comparableTarget ||
        comparableLabel === comparableTarget
      );
    });

    if (matched) {
      return matched;
    }
  }

  return null;
};

const resolveSelectedOption = (
  optionGroups: IrisSelectOptionGroup[],
  nextValue: string,
): HeroSelectOption => {
  const selectedOption = findOptionByValue(optionGroups, nextValue);
  const value = selectedOption?.value?.trim() || nextValue;
  const labelSource = selectedOption?.label?.trim() || nextValue;
  const label = labelSource.replace(/\s*\(\d+\)\s*$/, "").trim();

  return {
    value,
    label: value ? label : "",
  };
};

const ZIP_ERROR_MESSAGE = "Enter your ZIP Code";
const INVALID_ZIP_ERROR_MESSAGE = "Enter a valid ZIP Code";

/**
 * Convert reaxl-style select options into the option group structure required by Iris.
 */
const mapMakeOptionsToIrisOptionGroups = (
  options: (HeroSelectOption | HeroSelectOptionGroup)[],
): IrisSelectOptionGroup[] =>
  options.map((option) => {
    if ("optgroup" in option) {
      return {
        label: option.optgroup,
        children: option.options,
      };
    }

    return {
      children: [option],
    };
  });

/**
 * 4-Box show/hide wrapper for a hero SelectDropdown (desktop light + mobile inverse).
 * CSS toggles visibility so server and client HTML match — do NOT use useDevice().
 */
type HeroSelectProps = Readonly<{
  ariaLabel: string;
  label: string;
  name: string;
  value: string;
  onValueChange: (value: string) => void;
  optionGroups: IrisSelectOptionGroup[];
  placeholder: string;
  dataTestId: string;
}>;

function HeroSelect({
  ariaLabel,
  label,
  name,
  value,
  onValueChange,
  optionGroups,
  placeholder,
  dataTestId,
}: HeroSelectProps) {
  const sharedProps = {
    "aria-label": ariaLabel,
    label,
    name,
    value,
    onValueChange,
    optionGroups,
    placeholder,
    showQuantity: false,
  } as const;
  return (
    <>
      <Box
        className="iris:flex-1 iris:min-w-0 iris:hidden iris:md:block"
        data-testid={dataTestId}
      >
        <SelectDropdown
          className="blhr-search-dropdown iris:w-full iris:h-[48px] "
          {...sharedProps}
        />
      </Box>
      <Box
        className="iris:flex-1 iris:min-w-0 iris:block iris:md:hidden"
        data-testid={dataTestId}
      >
        <SelectDropdown
          className="blhr-search-dropdown iris:w-full iris:h-[48px]"
          {...sharedProps}
          inverse
        />
      </Box>
    </>
  );
}

export default function BuyerLandingHeroRedesign({
  currentZip = "",
  onZipChange,
}: BuyerLandingHeroRedesignProps) {
  const { selectedMake, setSelectedMake, makeOptions } = useMakeDropdown();

  const { selectedModel, setSelectedModel, modelOptions, updateModelOptions } =
    useModelDropdown();

  const makeOptionGroups = mapMakeOptionsToIrisOptionGroups(makeOptions);
  const modelOptionGroups = mapMakeOptionsToIrisOptionGroups(modelOptions);

  const [selectedMakeLabel, setSelectedMakeLabel] = useState("");
  const [selectedModelLabel, setSelectedModelLabel] = useState("");

  const [selectedZip, setSelectedZip] = useState(currentZip || "");
  const [lastValidZip, setLastValidZip] = useState(
    normalizeZip(currentZip || ""),
  );
  const [isZipRejected, setIsZipRejected] = useState(false);
  const [zipError, setZipError] = useState("");

  useEffect(() => {
    const normalizedCurrentZip = normalizeZip(currentZip || "");
    setSelectedZip(normalizedCurrentZip);
    setLastValidZip(normalizedCurrentZip);
    setIsZipRejected(false);
  }, [currentZip]);

  useEffect(() => {
    setSelectedModel("");
    setSelectedModelLabel("");

    if (!selectedMake) {
      setSelectedMakeLabel("");
      updateModelOptions([], "");
      return;
    }

    let isMounted = true;

    const loadModels = async () => {
      try {
        const models = await fetchModelsByMake(selectedMake);
        if (isMounted) {
          updateModelOptions(Array.isArray(models) ? models : [], selectedMake);
        }
      } catch (error) {
        console.error(`Error loading models for ${selectedMake}:`, error);
        if (isMounted) {
          updateModelOptions([], selectedMake);
        }
      }
    };

    loadModels();

    return () => {
      isMounted = false;
    };
  }, [selectedMake]);

  const buildShopUrl = (zipValue: string = selectedZip): string => {
    const makeSource = selectedMakeLabel || selectedMake;
    const modelSource = selectedModelLabel || selectedModel;
    return buildShopUrlUtil(shopPrivateSellerBaseUrl, makeSource, modelSource, zipValue);
  };

  const validateZipInput = (zipValue: string): boolean => {
    const isZipValid = isValidZip(zipValue);

    if (!zipValue) {
      setZipError(ZIP_ERROR_MESSAGE);
    }
    if (zipValue && !isZipValid) {
      setZipError(INVALID_ZIP_ERROR_MESSAGE);
    }

    return isZipValid;
  };

  const handleZipBlur = () => {
    validateZipInput(selectedZip);
  };

  const handleZipChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextZip = normalizeZip(event.target.value);
    setSelectedZip(nextZip);
    setIsZipRejected(false);

    if (!nextZip) {
      setZipError(ZIP_ERROR_MESSAGE);
      return;
    }

    if (nextZip.length < 5) {
      setZipError(INVALID_ZIP_ERROR_MESSAGE);
      return;
    }

    setZipError("");
    applyZipChange(nextZip);
  };

  const applyZipChange = async (nextZip: string) => {
    const locationData = await queryLocationByZip(nextZip);

    if (locationData?.error) {
      setIsZipRejected(true);
      return;
    }
    setIsZipRejected(false);
    setLastValidZip(nextZip);
    onZipChange?.(nextZip);
  };

  const handleSearchClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    const zipToUse = isZipRejected ? lastValidZip : selectedZip;

    if (!zipToUse && isZipRejected) {
      setZipError(INVALID_ZIP_ERROR_MESSAGE);
      return;
    }

    if (!zipToUse) {
      setZipError(ZIP_ERROR_MESSAGE);
      return;
    }
    
    if (!isValidZip(zipToUse)) {
      setZipError(INVALID_ZIP_ERROR_MESSAGE);
      return;
    }

    // Pixall Event C1. Search Cars Button
    const pixallData = {
      hasListingType: false,
      label: 'cta::srp::search',
      eventSource: 'button',
      pageId: 'slp_psx_buy',
      cdl: {
          event: {
              action: 'Click',
              element: 'Search Button',
              tagIndex: 'psx-120',
              search: {
                make: selectedMake,
                model: selectedModel,
                zipCode: zipToUse,
              }
          },
      },
    };
    sendClick('sycClickEvent', event, {
        isPixallEvent: true,
        pixallData,
    });

    queueMicrotask(() => {
      globalThis.location.href = buildShopUrl(zipToUse);
    });
  };

  const urlDesktop = heroImageDesktopUrl;

  return (
    <Box className="blhr-hero" data-testid="buyer-landing-hero-section">
      <picture>
        <source media="(min-width: 767px)" srcSet={urlDesktop} />
        <img
          className="blhr-background-image"
          alt="PSX Hero"
          src={urlDesktop}
          loading="eager"
          decoding="async"
          fetchPriority="high"
        />
      </picture>
      <Box className="blhr-overlay" />
      <Grid className="blhr-box">
        <GridItem className="iris:p-0">
          <Title
            as="h1"
            className="blhr-title"
          >
            Cars for Sale by Owner
          </Title>
          {/* iris-override (token unavailable): Body lacks responsive margin props; using iris:!mb overrides */}
          <Body
            as="p"
            className="iris:max-w-[557px] iris:max-[767px]:max-w-full iris:m-0 iris:!mb-[28px] iris:min-[768px]:mb-[28px] iris:max-[767px]:!mb-[32px] iris:text-[18px] iris:min-[768px]:text-[16px] iris:min-[1025px]:text-[18px] iris:font-normal iris:leading-[150%] iris:text-(--base-color-white)"
          >
            Autotrader has thousands of vehicles for sale by owners. You can
            even buy 100% online and skip the DMV.
          </Body>
        </GridItem>
        <GridItem className="iris:p-0">
          <Flex className="iris:flex-col iris:md:flex-row iris:md:flex-wrap iris:w-full iris:max-w-full iris:min-[1025px]:max-w-[903px] iris:gap-[17px]">
            <HeroSelect
              ariaLabel="Select Make"
              label="Select Make"
              name="makeCode"
              value={selectedMake}
              onValueChange={(nextValue) => {
                const { value, label } = resolveSelectedOption(
                  makeOptionGroups,
                  nextValue,
                );

                setSelectedMake(value);
                setSelectedMakeLabel(label);
              }}
              optionGroups={makeOptionGroups}
              placeholder="Any Make"
              dataTestId="make-dropdown"
            />
            <HeroSelect
              ariaLabel="Select Model"
              label="Select Model"
              name="modelCode"
              value={selectedModel}
              onValueChange={(nextValue) => {
                const { value, label } = resolveSelectedOption(
                  modelOptionGroups,
                  nextValue,
                );

                setSelectedModel(value);
                setSelectedModelLabel(label);
              }}
              optionGroups={modelOptionGroups}
              placeholder="Any Model"
              dataTestId="model-dropdown"
            />
            <Box className="iris:hidden iris:md:block">
              <TextInput
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={5}
                aria-label="Enter ZIP"
                error={Boolean(zipError)}
                errorMessage={zipError}
                data-testid="zip-input"
                className="iris:w-full iris:md:w-[156px]"
                wrapperClassName="blhr-zip-wrapper"
                placeholder="ZIP Code"
                value={selectedZip}
                onChange={handleZipChange}
                onBlur={handleZipBlur}
                required
              />
            </Box>
            <Box className="iris:block iris:md:hidden">
              <TextInput
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={5}
                aria-label="Enter ZIP"
                error={Boolean(zipError)}
                errorMessage={zipError}
                data-testid="zip-input-mobile"
                className="iris:w-full iris:md:w-[156px]"
                wrapperClassName="blhr-zip-wrapper"
                placeholder="ZIP Code"
                value={selectedZip}
                onChange={handleZipChange}
                onBlur={handleZipBlur}
                required
                inverse
              />
            </Box>
            {/* iris-override (token unavailable): Button lacks font-weight prop; using iris:!font-[700] */}
            <Button
              className="iris:w-full iris:md:w-[156px] iris:!font-[700]"
              inverse
              variant="primary"
              data-testid="hero-search-button"
              onClick={handleSearchClick}
            >
              Search
            </Button>
          </Flex>
        </GridItem>
      </Grid>
    </Box>
  );
}